import { LoadingOutlined } from "@ant-design/icons";
import { Button, Card, Col, Collapse, Dropdown, Input, Menu, Popconfirm, Row, Space, Tabs, Timeline } from "antd";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../assets/icons/icon";
import CustomCollapse from "../../../components/customCollapse";
import CustomCollapseHeader from "../../../components/customCollapseHeader";
import DividerLine from "../../../components/lineSplit";
import LoadingContainer from "../../../components/loadingContainer";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import { useTaxLegalId } from "../../../contexts/taxLegalCaseStore";
import _t from "../../../lang/translate";
import { deleteEntityRecording, patchEntityRecording, sendEntityRecording, taxLegalEntityRecordingUrl } from "../../../services/entityRecordingService";
import { useApi } from "../../../services/useApi";
import { PaginationedData } from "../../../types/apiTypes";
import { EnumItemType } from "../../../types/appTypes";
import { EntityRecording, EntityRecordingEventType, EntityRecordingId } from "../../../types/entityRecordingTypes";
import hexToRGB from "../../../utilities/colorUtil";
import format from "../../../utilities/formatNumbers";
import { AddCaseNote } from "../addCaseNote";
import TaxLegalCaseAttachments from "../taxLegalAttachments";
import TagFilter from "./tagFilter";
import TimelineItem from "./timelineItems/timelineItems";
import PopMaybeOver from "../../../components/popMaybeOver";
import { extractTokens, normalize } from "../../../utilities/stringExtraFunctions";
import PriceDifferenceTimeline from "./priceDifference";


const tags: EnumItemType[] = [
  // { title: "Status", id: 3, color: "#FA8C16" },
  { title: "Data", id: 4, color: "#1677FF" },
  { title: "Note", id: 1, color: "#5A6870" },
  { title: "Dok", id: 2, color: "#722ED1" },
  { title: "Økonomi", id: 5, color: "#52C41A" },
  { title: "Kunde", id: 6, color: "#EB2F96" },
  { title: "Besked", id: 7, color: "#EB2F96" },
]

function getEventTag(eventType: EntityRecordingEventType) {
  switch (eventType) {
    case "case_updated": return tags.find(item => item.id === 4);
    case "note_added": return tags.find(item => item.id === 1);
    case "message_added": return tags.find(item => item.id === 7);
    case "document_uploaded": return tags.find(item => item.id === 2);
    case "document_deleted": return tags.find(item => item.id === 2);
    case "receivable_offer_published":
    case "receivable_offer_sent":
    case "receivable_offer_accepted":
    case "receivable_invoice_created":
    case "receivable_invoice_sent":
    case "receivable_invoice_paid": return tags.find(item => item.id === 5);
    default: return tags.find(item => item.id === 6);
  }
}
function getEventConfig(eventType: EntityRecordingEventType) {
  switch (eventType) {
    case "case_updated": return {
      tag: getEventTag(eventType),
      canTogglePublic: false,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: false,
    }
    case "note_added": return {
      tag: getEventTag(eventType),
      canTogglePublic: true,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: true,
    }
    case "message_added": return {
      tag: getEventTag(eventType),
      canTogglePublic: false,
      canDeleteEvent: false,
      actionsEnabled: false,
      canSend: false,
    }
    case "document_uploaded": return {
      tag: getEventTag(eventType),
      canTogglePublic: false,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: false,
    }
    case "document_deleted": return {
      tag: getEventTag(eventType),
      canTogglePublic: false,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: false,
    }
    case "receivable_offer_published":
    case "receivable_offer_sent":
    case "receivable_offer_accepted":
    case "receivable_invoice_created":
    case "receivable_invoice_sent":
    case "receivable_invoice_paid": return {
      tag: getEventTag(eventType),
      canTogglePublic: true,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: true,
    }
    default: return {
      tag: getEventTag(eventType),
      canTogglePublic: false,
      canDeleteEvent: true,
      actionsEnabled: true,
      canSend: true,
    }
  }
}

function toggleSet(setter: (value: React.SetStateAction<Set<EntityRecordingId>>) => void, id: EntityRecordingId, edit: boolean) {
  setter((prev) => {
    const newSet = new Set(prev)
    if (edit) newSet.add(id);
    else newSet.delete(id);
    return newSet
  })
}

export default function CaseTimeLine() {
  const caseId = useTaxLegalId()
  const [{ data, isError, isLoading: isLoadingData }, setUrl, setData] = useApi<PaginationedData<EntityRecording>>(taxLegalEntityRecordingUrl(caseId), { data: [] })
  const isFirstLoad = useRef(true)

  const [search, setSearch] = useState('')
  const [seeCustomerView, setSeeCustomerView] = useState(false)
  const [tagFilterIds, setTagFilterIds] = useState<Set<number>>(new Set())

  const [editingIds, setEditingIds] = useState<Set<EntityRecordingId>>(new Set())
  const [loadingIds, setLoadingIds] = useState<Set<EntityRecordingId>>(new Set())

  useEffect(() => {
    if (isFirstLoad.current && data.data.length && !isLoadingData) {
      const hasNote = data.data.some(event => event.eventType === 'note_added');
      setTagFilterIds(new Set([hasNote ? 1 : null].filter(Boolean) as number[]));
      isFirstLoad.current = false;
    }
  }, [isLoadingData, data])

  const debounceSetSearch = useCallback(
    debounce((val: string) => {
      setSearch(val)
    }, 250), [])

  function applyTagFilter(tag?: EnumItemType) {
    if (!tag?.id) return
    setTagFilterIds((prev) => {
      const newTagFilterIds = new Set(prev)
      if (newTagFilterIds.has(tag.id)) {
        newTagFilterIds.delete(tag.id)
      } else {
        newTagFilterIds.add(tag.id)
      }
      return newTagFilterIds
    })
  }

  const toggleEditing = (id: EntityRecordingId, edit: boolean) => toggleSet(setEditingIds, id, edit);
  const toggleLoading = (id: EntityRecordingId, edit: boolean) => toggleSet(setLoadingIds, id, edit);

  function refreshData() {
    setUrl(taxLegalEntityRecordingUrl(caseId) + "?refreshTimestamp=" + Date.now())
  }

  async function deleteEvent(id: EntityRecordingId) {
    try {
      toggleLoading(id, true)
      await deleteEntityRecording(id)
      setData(prev => ({ ...prev, data: prev.data.filter(item => item.id !== id) }))
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(id, false)
    }
  }
  async function togglePublicEntity(event: EntityRecording) {
    try {
      toggleLoading(event.id, true)
      const { data } = await patchEntityRecording(event.id ?? -1, { isPublic: !event.isPublic })
      setData(prev => ({ ...prev, data: prev.data.map(item => item.id === data.data.id ? data.data : item) }))
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(event.id, false)
    }
  }
  async function sendEvent(event: EntityRecording) {
    try {
      toggleLoading(event.id, true)
      await sendEntityRecording(event.id ?? -1, {})
      setData(prev => ({ ...prev, data: prev.data.map(item => item.id === event.id ? { ...event, notifiedAt: new Date().toISOString() } : item) }))
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(event.id, false)
    }
  }


  function onSubmit(event: EntityRecording) {
    setData(prev => ({ ...prev, data: [event, ...prev.data] }))
  }
  const eventItems = useMemo(() => {
    let filtered = data.data
    if (seeCustomerView) {
      filtered = filtered.filter(item => item.isPublic)
    }
    if (tagFilterIds.size) {
      filtered = filtered.filter(item => {
        const tag = getEventTag(item.eventType)
        return tagFilterIds.has(tag?.id ?? -1)
      })
    }
    if (search) {
      const searchTokens = extractTokens(search)
      filtered = filtered.filter(item => {
        const searchAble = normalize([
          item.displayText ?? "",
          item.creator?.fullName ?? "",
          (item.eventType === 'document_uploaded' || item.eventType === 'document_deleted') ? item.payload?.fileName ?? "" : "",
          // @ts-expect-error Property 'paymentTerms' does not exist on type
          item.eventType.includes('receivable') ? item.payload?.paymentTerms ?? "" : "",
          // @ts-expect-error Property 'title' does not exist on type
          item.eventType.includes('receivable') ? item.payload?.title ?? "" : "",
          item.eventType === 'message_added' || item.eventType === 'note_added' ? item.payload?.content ?? "" : "",
        ].join(' '))
        return searchTokens.every(token => searchAble.includes(token))
      })
    }
    return filtered
  }, [search, data.data, seeCustomerView, tagFilterIds])

  return (
    <Card title={null}>
      <Row justify="space-between" align="middle">
        <Col><h3>{_t("activities")}</h3></Col>
        <Col>
          <Space style={{ columnGap: 6 }}>
            <span>{_t("filters")}:</span>
            {tags
              .filter(tag => data?.data?.some(e => getEventTag(e.eventType)?.id === tag.id))
              .map(tag => <TagFilter
                key={tag.id} tag={tag} onClick={() => applyTagFilter(tag)}
                active={tagFilterIds.size ? tagFilterIds.has(tag.id) : undefined}
                notActive={tagFilterIds.size ? !tagFilterIds.has(tag.id) : undefined}
                disabled={!data?.data?.some(e => getEventTag(e.eventType)?.id === tag.id)}
              />)}
          </Space>
        </Col>
      </Row>
      <DividerLine fluid />

      <Input prefix={<Icon name="search" size="large" />} onChange={(e) => debounceSetSearch(e.target.value)} placeholder={_t("search") + "..."} />

      <AddTimelineActivity onSubmit={onSubmit} />

      <div>
        <Timeline>
          <Timeline.Item dot={isLoadingData ? (<LoadingOutlined />) : (<Icon name="refresh-circle-outline" size="huge" />)} >
            <Row justify="space-between" align="middle">
              <Col>
                <Button disabled={isLoadingData} onClick={refreshData} type="link" className="pb-1 pt-0 pl-0">{_t("refresh")}</Button>
              </Col>
              <Col>
                <Button type="text" size="small"
                  onClick={() => setSeeCustomerView(!seeCustomerView)}
                  icon={
                    <Icon size="medium"
                      onClick={seeCustomerView ? () => undefined : undefined}
                      className={seeCustomerView ? "text-primary" : "text-secondary"}
                      name={seeCustomerView ? "eye" : "eye-closed"}
                    />
                  }
                  className="btn-grey text-normal text-smaller"
                >
                  {_t("customer_view")}
                </Button>
              </Col>
            </Row>
          </Timeline.Item>

          <LoadingContainer loading={isLoadingData}>
            {eventItems?.map(event => {
              const { actionsEnabled, tag, canTogglePublic, canDeleteEvent, canSend } = getEventConfig(event.eventType)

              if (event.eventType === 'note_added') {
              }
              return (
                <Timeline.Item key={event.id} color={tag?.color}>
                  <LoadingContainer loading={loadingIds.has(event.id)}>
                    <div className="border-radius pl-05 pr-05 pt-03 pb-05 border-light" style={{
                      backgroundColor: hexToRGB(tag?.color ?? "", 0.03),
                      borderColor: hexToRGB(tag?.color ?? "", 0.08),
                    }}>
                      <Row justify="space-between" align="middle" className="mb-05">
                        <Col><small>
                          {event.creator?.fullName && (
                            <span className="text-dark text-medium mr-05">{event.creator?.fullName}</span>
                          )}
                          {event.isAutoGenerated && (
                            <>
                              <Icon name="sync-outline" /> {_t("auto_generated")}
                            </>
                          )}
                          {' '}
                          &mdash; {format.dateAndTime(event.createdAt)}
                        </small></Col>
                        <Col>
                          <TagFilter
                            key={tag?.id} tag={tag ?? null} onClick={() => applyTagFilter(tag)}
                            active={tagFilterIds.size ? tagFilterIds.has(tag?.id ?? -1) : undefined}
                          />
                        </Col>
                      </Row>

                      <TimelineItem event={event} editing={editingIds.has(event.id)} cancelEditing={() => toggleEditing(event.id, false)} />

                      {actionsEnabled && (
                        <Space className="w-100 mt-05" size={"small"}>
                          {/* <Icon onClick={canEdit ? () => toggleEditing(event.id, !editingIds.has(event.id)) : undefined} size="medium" className="text-secondary" name={editingIds.has(event.id) ? "pencil-simple-slash" : "pencil-simple"} /> */}
                          <Icon onClick={canTogglePublic ? () => togglePublicEntity(event) : undefined} size="medium"
                            className={event.isPublic ? "text-primary" : "text-secondary"}
                            name={event.isPublic ? "eye" : "eye-closed"}
                          />
                          <PopMaybeOver content={event.notifiedAt ? "Sendt den " + format.dateAndTime(event.notifiedAt) : undefined}>
                            {canSend ? (
                              <Popconfirm title={event.notifiedAt ? _t("msg.confirm_send_to_customer_again") : _t("msg.confirm_send_to_customer")} onConfirm={() => sendEvent(event)}>
                                <Icon size="medium"
                                  className={event.notifiedAt ? "text-green" : "text-secondary"}
                                  name={event.notifiedAt ? "envelope-open" : "envelope"}
                                />
                              </Popconfirm>
                            ) : (
                              <Icon size="medium"
                                className={event.notifiedAt ? "text-green" : "text-secondary"}
                                name={event.notifiedAt ? "envelope-open" : "envelope"}
                              />
                            )}
                          </PopMaybeOver>
                          <Dropdown
                            overlay={
                              <Menu>
                                <Menu.Item key="delete" disabled={!canDeleteEvent} danger icon={<Icon name="trash-bin-outline" size="small" />} onClick={() => deleteEvent(event.id)}>
                                  {_t("delete")}
                                </Menu.Item>
                              </Menu>
                            }
                            trigger={['click']}
                          >
                            <Icon size="medium" className={"text-secondary"} name={"ellipsis-vertical-sharp"} />
                          </Dropdown>
                        </Space>
                      )}
                    </div>
                  </LoadingContainer>
                </Timeline.Item>
              )
            })}
          </LoadingContainer>

        </Timeline>
      </div>
    </Card>
  )
}

type AddTimelineActivityProps = {
  onSubmit: (event: EntityRecording) => void
}
function AddTimelineActivity({ onSubmit }: AddTimelineActivityProps) {
  const [activeKey, setActiveKey] = useState<string[]>(['open'])

  return (
    <CustomCollapse activeKey={activeKey} setActiveKey={setActiveKey} className="mr-0 ml-0 mt-2 mb-2">
      <Collapse.Panel key={"open"} header={<CustomCollapseHeader>{_t("actions")}</CustomCollapseHeader>}>
        <Tabs defaultActiveKey="offer" className="tax-legal-timeline-tabs" >
          <Tabs.TabPane key={"note"} tab={_t("note")}>
            <AddCaseNote onSubmit={onSubmit} />
          </Tabs.TabPane>
          {/* <Tabs.TabPane key="documents" tab={_t("documents")}> */}
          {/*   <TaxLegalCaseAttachments /> */}
          {/* </Tabs.TabPane> */}


          <Tabs.TabPane key="regulation_timeline" tab={_t("regulations")}>
            <PriceDifferenceTimeline />
          </Tabs.TabPane>
        </Tabs>
      </Collapse.Panel>
    </CustomCollapse>
  )
}
